// ===========================================
// AppLocalizationProvider
// ===========================================
// The ONLY way this app should mount a date-fns MUI picker context.
//
// Why this exists rather than an `adapterLocale` prop at each call site:
// `AdapterDateFns` defaults to en-US when no locale is supplied
// (`AdapterDateFns.js`: `import defaultLocale from 'date-fns/locale/en-US'`).
// Thirteen call sites each mounted their own bare `<LocalizationProvider>`, so
// every picker in the app rendered and PARSED as MM/DD/YYYY — against Critical
// Rule 7 (user-facing dates are dd/mm/yyyy, en-AU). Owning the locale here
// removes the opportunity to forget it a fourteenth time.
//
// ⚠️ "Thirteen" is not a typo for the plan's twelve: a thirteenth appeared on
// another branch WHILE this fix was being written. That is the whole argument
// for a wrapper over a per-call-site prop.
//
// ⚠️ This changes the PARSER, not just the display. `05/09/2026` is silently
// valid in both orders — 5 September under en-AU, 9 May under en-US — so a
// wrong locale here produces a wrong date with no error anywhere.
//
// The picker field format is not hard-coded by MUI: it uses date-fns' locale
// -driven `P` token (`AdapterDateFnsBase.js`: keyboardDate: 'P'), which resolves
// to dd/MM/yyyy under enAU and MM/dd/yyyy under enUS. So supplying the locale
// fixes display, input mask and parsing together, with no per-picker `format`.
//
// Usage — a drop-in replacement for `<LocalizationProvider dateAdapter={...}>`:
//   <AppLocalizationProvider>
//     <DatePicker ... />
//   </AppLocalizationProvider>

import { ReactNode } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// ⚠️ date-fns v2 named BARREL import — keep it this way.
// The deep path 'date-fns/locale/en-AU' does resolve in 2.30.0, which makes it
// look like a valid "modernisation", but it exports NEITHER a named `enAU` NOR
// a `default` (measured: both `undefined`). Switching to it hands the adapter
// `undefined`, which silently falls back to en-US — the exact bug this file
// exists to prevent, restored with no error. A deep import also invites the
// `styled_default is not a function` pre-bundling trap (docs/gotchas.md).
// The barrel costs nothing: Rollup tree-shakes it (measured 4,324.00 kB vs
// 4,324.17 kB for the deep form — the barrel is marginally SMALLER).
import { enAU } from 'date-fns/locale';

export interface AppLocalizationProviderProps {
  children: ReactNode;
}

export function AppLocalizationProvider({ children }: AppLocalizationProviderProps) {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enAU}>
      {children}
    </LocalizationProvider>
  );
}

export default AppLocalizationProvider;
